/**
 * src/auth/msalClient.ts
 *
 * Singleton MSAL — extrait de AuthContext.tsx pour être importable aussi par d'autres modules sans
 * dépendre d'un composant React (ex. authFetch.ts).
 *
 * Gère aussi le **token de session interne** émis par report-api (POST /api/auth/login, cf.
 * server/lib/auth.ts) : c'est CE token — pas l'ID token MSAL brut — qui est envoyé en Bearer sur
 * toutes les routes protégées (cf. authFetch.ts). Après connexion MSAL (ssoSilent/loginPopup, cf.
 * AuthContext.tsx::loginMicrosoft()), exchangeForSessionToken() échange l'ID token MSAL contre ce
 * token interne UNE SEULE FOIS ; il est ensuite réutilisé tel quel (persisté en sessionStorage,
 * survit aux rechargements de page) jusqu'à son expiration — même durée de vie que l'ID token MSAL
 * d'origine, ~1h, cf. server/lib/auth.ts::mintSessionToken().
 *
 * Pourquoi ne pas rafraîchir via acquireTokenSilent à CHAQUE requête (au lieu du token en cache
 * ci-dessus, relu de façon synchrone) : observé en pratique que le renouvellement silencieux (iframe
 * cachée vers login.microsoftonline.com) échoue de façon fiable dans certains environnements
 * (`BrowserAuthError: monitor_window_timeout` — cookies tiers bloqués côté navigateur, ou politique
 * du tenant Entra qui interdit le SSO silencieux en iframe). getSessionToken() reste donc synchrone
 * et sans appel MSAL pour le chemin critique de chaque requête — mais AuthContext.tsx tente
 * ssoSilent en tâche de fond à deux moments (cf. sa docstring) : un rafraîchissement périodique
 * (10 min) et un retry unique sur 401 (via trySilentReauth() ci-dessous, appelé par authFetch.ts) —
 * dans les environnements où ssoSilent échoue, ces deux tentatives échouent tout aussi silencieusement
 * et l'app retombe sur le comportement d'origine : le token en cache reste utilisé jusqu'à sa vraie
 * expiration, l'utilisateur se reconnecte alors manuellement (bandeau d'expiration, cf. App.tsx).
 */

import { PublicClientApplication } from "@azure/msal-browser";
import { msalConfig } from "./msalConfig";
import { APP_CONFIG } from "../config/app.config";
import type { UserRole } from "./roles";

let _msalInstance: PublicClientApplication | null = null;
export function getMsal(): PublicClientApplication {
  if (!_msalInstance) _msalInstance = new PublicClientApplication(msalConfig);
  return _msalInstance;
}

// ── Token de session interne ──────────────────────────────────────────────────

const SESSION_TOKEN_STORAGE_KEY = "report_app_session_token";

export interface SessionUser { email: string; name: string; role: UserRole; }

/** Échange l'ID token MSAL (obtenu au login) contre un token de session interne. */
export async function exchangeForSessionToken(idToken: string): Promise<{ token: string; user: SessionUser }> {
  const res = await fetch(`${APP_CONFIG.apiBaseUrl}/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ idToken }),
  });
  if (!res.ok) {
    const body = await res.text().catch(() => res.statusText);
    throw new Error(`Échange du token de session échoué (${res.status}) : ${body}`);
  }
  return res.json() as Promise<{ token: string; user: SessionUser }>;
}

export function setSessionToken(token: string | null | undefined): void {
  if (token) sessionStorage.setItem(SESSION_TOKEN_STORAGE_KEY, token);
  else sessionStorage.removeItem(SESSION_TOKEN_STORAGE_KEY);
}

/** Décode le payload d'un JWT (sans vérifier la signature — usage local uniquement, le serveur revérifie tout). */
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const payload = token.split(".")[1];
    return JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
  } catch {
    return null;
  }
}

/** Marge de 60s pour éviter d'utiliser un token qui expire pendant le trajet réseau. */
function isStillValid(token: string): boolean {
  const exp = decodeJwtPayload(token)?.exp;
  return typeof exp === "number" && exp * 1000 > Date.now() + 60_000;
}

/**
 * Token de session interne en cache, ou null s'il est absent/expiré. Envoyé en Bearer par
 * authFetch.ts sur toute requête protégée — sans appel MSAL, synchrone, jamais de dépendance à
 * acquireTokenSilent pour le trafic courant (cf. docstring de fichier).
 */
export function getSessionToken(): string | null {
  const token = sessionStorage.getItem(SESSION_TOKEN_STORAGE_KEY);
  return token && isStillValid(token) ? token : null;
}

/** Utilisateur (email/name/role) porté par le token de session en cache, ou null si absent/expiré. */
export function getCachedSessionUser(): SessionUser | null {
  const token = getSessionToken();
  if (!token) return null;
  const payload = decodeJwtPayload(token);
  const email = payload?.email as string | undefined;
  const role = payload?.role as UserRole | undefined;
  if (!email || !role) return null;
  return { email, name: (payload?.name as string) ?? email, role };
}

/**
 * Timestamp (ms) d'expiration du token de session en cache, ou null si absent/invalide — lu même si
 * le token est déjà expiré (contrairement à getSessionToken()), pour permettre à AuthContext.tsx
 * d'afficher un compte à rebours fidèle à la vraie durée de vie (~1h) plutôt qu'au compteur client
 * "12h" indépendant (cf. docs/authentification.md, section Gestion de la session).
 */
export function getSessionTokenExpiresAt(): number | null {
  const token = sessionStorage.getItem(SESSION_TOKEN_STORAGE_KEY);
  if (!token) return null;
  const exp = decodeJwtPayload(token)?.exp;
  return typeof exp === "number" ? exp * 1000 : null;
}

// ── Pont vers authFetch.ts (hors arbre React) ─────────────────────────────────
//
// authFetch.ts wrappe window.fetch en dehors de tout composant React (installé une fois dans
// main.tsx) : il ne peut donc pas appeler useAuthContext(). AuthContext.tsx enregistre ici, au
// montage de AuthProvider, une fonction de rafraîchissement silencieux (ssoSilent uniquement,
// jamais de popup — ce fetch peut être déclenché hors de tout geste utilisateur, un loginPopup()
// sans clic serait bloqué par le navigateur) et un callback de notification "session invalide",
// pour que authFetch.ts puisse réagir à un 401 sans dupliquer la logique MSAL.

type SilentReauthFn = () => Promise<boolean>;
let _silentReauth: SilentReauthFn | null = null;
let _inFlightSilentReauth: Promise<boolean> | null = null;

/** Appelé par AuthContext.tsx au montage/démontage du provider. */
export function registerSilentReauth(fn: SilentReauthFn | null): void { _silentReauth = fn; }

/**
 * Cœur dédupliqué, partagé par trySilentReauth() et trySilentReauthQuiet() ci-dessous : peu importe
 * qui déclenche le premier appel (retry réactif sur 401, ou timer périodique proactif,
 * cf. AuthContext.tsx), un seul ssoSilent part réellement ; tout appelant concurrent réutilise la
 * même promesse.
 */
function startOrJoinSilentReauth(): Promise<boolean> {
  if (!_silentReauth) return Promise.resolve(false);
  if (!_inFlightSilentReauth) {
    _inFlightSilentReauth = _silentReauth().finally(() => { _inFlightSilentReauth = null; });
  }
  return _inFlightSilentReauth;
}

/**
 * authFetch.ts (retry sur 401) : dédupliqué (cf. startOrJoinSilentReauth) et pilote le
 * <LoadingScreen/> global (notifyReconnecting(), cf. App.tsx) autour de SA PROPRE attente — pas de
 * l'opération dédupliquée elle-même. Si un rafraîchissement périodique quiet (trySilentReauthQuiet())
 * est déjà en vol quand ce retry le rejoint, le bandeau apparaît quand même à cet instant (l'appelant
 * attend réellement une réponse), puis disparaît à la résolution — sans déclencher un second ssoSilent.
 */
export async function trySilentReauth(): Promise<boolean> {
  notifyReconnecting(true);
  try {
    return await startOrJoinSilentReauth();
  } finally {
    notifyReconnecting(false);
  }
}

/**
 * Rafraîchissement périodique proactif (AuthContext.tsx) : même dédup que trySilentReauth(), mais
 * jamais de <LoadingScreen/> — ce rafraîchissement n'est l'attente d'aucune requête utilisateur,
 * il doit rester invisible tant qu'il réussit (cf. docs/authentification.md).
 */
export function trySilentReauthQuiet(): Promise<boolean> {
  return startOrJoinSilentReauth();
}

type Notifier = () => void;
let _notifySessionInvalid: Notifier | null = null;

/** Appelé par AuthContext.tsx au montage/démontage du provider. */
export function registerSessionInvalidNotifier(fn: Notifier | null): void { _notifySessionInvalid = fn; }

/** authFetch.ts : le rafraîchissement silencieux a échoué — plus aucune requête protégée n'aboutira sans reconnexion interactive. */
export function notifySessionInvalid(): void { _notifySessionInvalid?.(); }

type ReconnectingNotifier = (reconnecting: boolean) => void;
let _notifyReconnecting: ReconnectingNotifier | null = null;

/** Appelé par AuthContext.tsx au montage/démontage du provider. */
export function registerReconnectingNotifier(fn: ReconnectingNotifier | null): void { _notifyReconnecting = fn; }

/** trySilentReauth() ci-dessus : signale le début/fin du rafraîchissement réactif (401 déjà survenu) — jamais pour le rafraîchissement périodique proactif, qui reste invisible tant qu'il réussit. */
export function notifyReconnecting(reconnecting: boolean): void { _notifyReconnecting?.(reconnecting); }
